import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";
import EquationBalancer from "./pages/EquationBalancer.tsx";
import MolarMassCalculator from "./pages/MolarMassCalculator.tsx";
import SolubilityTable from "./pages/SolubilityTable.tsx";
import IonEngine from "./pages/IonEngine.tsx";
import BohrModel from "./pages/BohrModel.tsx";
import WorksheetGenerator from "./pages/WorksheetGenerator.tsx";
import DimensionalAnalysis from "./pages/DimensionalAnalysis.tsx";
import Electrochemistry from "./pages/Electrochemistry.tsx";
import OrganicPrimer from "./pages/OrganicPrimer.tsx";
import Kinetics from "./pages/Kinetics.tsx";
import Equilibrium from "./pages/Equilibrium.tsx";
import OnlineLab from "./pages/OnlineLab.tsx";
import VirtualLabs from "./pages/VirtualLabs.tsx";
import OrganicChemistry from "./pages/OrganicChemistry.tsx";
import LessonPlans from "./pages/LessonPlans.tsx";
import EndOfYearProject from "./pages/EndOfYearProject.tsx";
import Explore from "./pages/Explore.tsx";
import MoleculeViewer from "./pages/MoleculeViewer.tsx";
import PlayHub from "./pages/PlayHub.tsx";
import ElementBattle from "./pages/games/ElementBattle.tsx";
import ChemDuel from "./pages/games/ChemDuel.tsx";
import SpeedQuiz from "./pages/games/SpeedQuiz.tsx";
import ReactionRace from "./pages/games/ReactionRace.tsx";
import LabSafetyShowdown from "./pages/games/LabSafetyShowdown.tsx";
import GlasswareId from "./pages/games/GlasswareId.tsx";
import PhSort from "./pages/games/PhSort.tsx";
import PeriodicSprint from "./pages/games/PeriodicSprint.tsx";
import BondTypeSnap from "./pages/games/BondTypeSnap.tsx";
import ElectronConfigSprint from "./pages/games/ElectronConfigSprint.tsx";
import ChemDoku from "./pages/games/ChemDoku.tsx";
import LearnHub from "./pages/learn/LearnHub.tsx";
import StudySession from "./pages/learn/StudySession.tsx";
import PhaseChangeLab from "./pages/learn/PhaseChangeLab.tsx";
import CollisionSim from "./pages/learn/CollisionSim.tsx";
import CrystalLattices from "./pages/learn/CrystalLattices.tsx";
import OrbitalGallery from "./pages/learn/OrbitalGallery.tsx";
import DailyElement from "./pages/DailyElement.tsx";
import AccessibilityStatement from "./pages/AccessibilityStatement";
import About from "./pages/About";
import Privacy from "./pages/Privacy";
import AccessibilityWidget from "./components/AccessibilityWidget";
import SiteFooter from "./components/SiteFooter";
import PrivacyNoticeModal from "./components/PrivacyNoticeModal";
import ScrollToTop from "./components/ScrollToTop";


const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/tools/equation-balancer" element={<EquationBalancer />} />
          <Route path="/tools/molar-mass" element={<MolarMassCalculator />} />
          <Route path="/tools/solubility" element={<SolubilityTable />} />
          <Route path="/ions" element={<IonEngine />} />
          <Route path="/atom" element={<BohrModel />} />
          <Route path="/worksheet" element={<WorksheetGenerator />} />
          <Route path="/tools/dimensional-analysis" element={<DimensionalAnalysis />} />
          <Route path="/learn/electrochemistry" element={<Electrochemistry />} />
          <Route path="/learn/organic-primer" element={<OrganicPrimer />} />
          <Route path="/learn/kinetics" element={<Kinetics />} />
          <Route path="/learn/equilibrium" element={<Equilibrium />} />
          <Route path="/learn/phase-changes" element={<PhaseChangeLab />} />
          <Route path="/learn/kinetics/collisions" element={<CollisionSim />} />
          <Route path="/learn/crystal-lattices" element={<CrystalLattices />} />
          <Route path="/learn/orbitals" element={<OrbitalGallery />} />
          <Route path="/lab" element={<OnlineLab />} />
          <Route path="/lab/virtual" element={<VirtualLabs />} />
          <Route path="/organic" element={<OrganicChemistry />} />
          <Route path="/tools/molecule-viewer" element={<MoleculeViewer />} />
          <Route path="/lessons" element={<LessonPlans />} />
          <Route path="/teach/end-of-year-project" element={<EndOfYearProject />} />
          <Route path="/play" element={<PlayHub />} />
          <Route path="/play/element-battle" element={<ElementBattle />} />
          <Route path="/play/chem-duel" element={<ChemDuel />} />
          <Route path="/play/speed-quiz" element={<SpeedQuiz />} />
          <Route path="/play/reaction-race" element={<ReactionRace />} />
          <Route path="/play/lab-safety-showdown" element={<LabSafetyShowdown />} />
          <Route path="/play/glassware-id" element={<GlasswareId />} />
          <Route path="/play/ph-sort" element={<PhSort />} />
          <Route path="/play/periodic-sprint" element={<PeriodicSprint />} />
          <Route path="/play/bond-type-snap" element={<BondTypeSnap />} />
          <Route path="/play/electron-config-sprint" element={<ElectronConfigSprint />} />
          <Route path="/play/chem-doku" element={<ChemDoku />} />
          <Route path="/learn" element={<LearnHub />} />
          <Route path="/learn/:deckId" element={<StudySession />} />
          <Route path="/daily" element={<DailyElement />} />
          <Route path="/accessibility" element={<AccessibilityStatement />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <SiteFooter />
        <AccessibilityWidget />

        <PrivacyNoticeModal />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
